/**
 * Types for the Invite Teammates admin page.
 *
 * Two seat pools (aligned with AE-195 / `count_license_pool_used`):
 *   - `leader` — leader + district_leader personas
 *   - `staff`  — teacher + support_staff (+ classified_staff) seats
 *
 * DB statuses: pending | accepted | expired | cancelled.
 * UI may label `pending` as "Sent". Sample path still used when live reads fail.
 */

/** Roles the invite form can assign — subset of `WorkspacePersona`. */
export type InviteRole = "leader" | "teacher" | "support_staff";

/** Pool an invite/role consumes — leader is its own pool, the other two share `staff`. */
export type InviteSeatPool = "leader" | "staff";

export const INVITE_ROLE_TO_POOL: Record<InviteRole, InviteSeatPool> = {
  leader: "leader",
  teacher: "staff",
  support_staff: "staff",
};

export const INVITE_ROLE_LABEL: Record<InviteRole, string> = {
  leader: "Leader",
  teacher: "Teacher",
  support_staff: "Support Staff",
};

export const INVITE_POOL_LABEL: Record<InviteSeatPool, string> = {
  leader: "Leader seats",
  staff: "Staff seats",
};

export interface LicensePool {
  /** Total seats provisioned for this pool. */
  total: number;
  /**
   * Active members + still-pending (non-expired) invites — both count against
   * availability so a leader can't outpace their cap by spamming invites.
   */
  used: number;
}

export interface LicenseSummary {
  /** District the counts belong to (or null when scope is school-only / sample). */
  districtId: string | null;
  /** School the counts belong to when the leader is school-scoped. */
  schoolId: string | null;
  /** Friendly label rendered in the page header (school name or district name). */
  scopeLabel: string;
  pools: Record<InviteSeatPool, LicensePool>;
}

/** Canonical DB + UI invite status (AE-195). UI labels `pending` as "Sent". */
export type PendingInviteStatus = "pending" | "accepted" | "expired" | "cancelled";

export interface PendingInvite {
  id: string;
  email: string;
  role: InviteRole;
  /** School the invitee will join when accepted; null for district-level leaders. */
  schoolId: string | null;
  schoolName: string | null;
  status: PendingInviteStatus;
  /** ISO timestamp; surfaced as "Sent X days ago". Maps to `pending_invites.sent_at`. */
  sentAt: string;
  /** Email of the leader that initiated the invite, for audit trail. */
  invitedByEmail: string | null;
}

/** Input the form submits to `sendInvite`. */
export interface SendInviteInput {
  email: string;
  role: InviteRole;
  /** Required for district-scoped leaders; school-scoped leaders pass their own school id. */
  schoolId: string;
  schoolName: string;
}

export interface SendInviteResult {
  ok: boolean;
  /** Populated when `ok=false` — surfaced inline + as a toast. */
  errorMessage?: string;
  /** New row appended to the pending list when `ok=true`. */
  invite?: PendingInvite;
}

/** Result shape for cancel/resend — same success/error contract without a row payload. */
export interface InviteActionResult {
  ok: boolean;
  /** Populated when `ok=false` — surfaced as a toast. */
  errorMessage?: string;
}
